import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { useLocation } from 'react-router-dom';
import { dataAPI } from '../../helpers/api_service';
import StatsOverview from './StatsOverview';
import AssignedProjectsTable from './AssignedProjectsTable';
import TodaysTasksCalendar from './TodaysTasksCalendar';
import TasksUpdateChart from './TasksUpdateChart';
import DocumentStorageOverview from './DocumentStorageOverview';
import NotificationsPanel from './NotificationsPanel';
import TrialExpiryNotice from '../../components/TrialExpiryNotice';
import './Dashboard.css';

function Dashboard() {
  const { mode } = useThemeMode();
  const location = useLocation();
  const [dashboardData, setDashboardData] = useState(null);
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [todaysTasks, setTodaysTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [subscriptionPackage, setSubscriptionPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const isInitialMount = useRef(true);

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Call all 4 dashboard APIs in parallel
      const [
        dashboardResponse,
        assignedProjectsResponse,
        todaysTasksResponse,
        notificationsResponse
      ] = await Promise.all([
        dataAPI.getDashboard(),
        dataAPI.getAssignedProjects(),
        dataAPI.getTodaysTasks(),
        dataAPI.getNotifications({ showLoading: false })
      ]);


      // Update state with API responses
      if (dashboardResponse.status === 'success') {
        setDashboardData(dashboardResponse.data);
        
        // Extract subscription package if available
        if (dashboardResponse.data && dashboardResponse.data.subscription_package) {
          setSubscriptionPackage(dashboardResponse.data.subscription_package);
        }

        // Update userData in localStorage with permissions from dashboard response
        if (dashboardResponse.data && dashboardResponse.data.permissions) {
          try {
            const existingUserData = JSON.parse(localStorage.getItem('userData') || '{}');
            const updatedUserData = {
              ...existingUserData,
              permissions: dashboardResponse.data.permissions
            };
            localStorage.setItem('userData', JSON.stringify(updatedUserData));
            
            // Dispatch event to notify other components of the update
            window.dispatchEvent(new CustomEvent('userProfileUpdated', {
              detail: updatedUserData
            }));
            
            console.log('Updated userData with permissions:', updatedUserData.permissions);
          } catch (error) {
            console.error('Error updating userData with permissions:', error);
          }
        }
      }
      
      if (assignedProjectsResponse.status === 'success') {
        setAssignedProjects(assignedProjectsResponse.data || []);
      }
      
      if (todaysTasksResponse.status === 'success') {
        setTodaysTasks(todaysTasksResponse.data || []);
      }
      
      if (notificationsResponse.status === 'success') {
        setNotifications(notificationsResponse.data || []);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // The API service will handle 401 errors automatically
      // Other errors can be handled here if needed
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all dashboard data when component mounts
  useEffect(() => {
    fetchDashboardData();
    isInitialMount.current = false;
  }, [fetchDashboardData]);

  // Auto-refresh when user navigates back to the dashboard page
  useEffect(() => {
    // Skip the initial mount to avoid double fetch
    if (isInitialMount.current) return;

    // Only refresh if we're on the dashboard route
    if (location.pathname === '/dashboard') {
      fetchDashboardData();
    }
  }, [location.pathname, fetchDashboardData]);

  // Auto-refresh when page becomes visible or window gains focus
  useEffect(() => {
    let lastVisibilityChange = Date.now();
    const MIN_REFRESH_INTERVAL = 30000; // 30 seconds minimum between refreshes

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && location.pathname === '/dashboard') {
        const now = Date.now();
        // Only refresh if enough time has passed since last refresh
        if (now - lastVisibilityChange > MIN_REFRESH_INTERVAL) {
          lastVisibilityChange = now;
          fetchDashboardData();
        }
      }
    };

    const handleFocus = () => {
      if (location.pathname === '/dashboard') {
        const now = Date.now();
        // Only refresh if enough time has passed since last refresh
        if (now - lastVisibilityChange > MIN_REFRESH_INTERVAL) {
          lastVisibilityChange = now;
          fetchDashboardData();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchDashboardData, location.pathname]);

  if (loading) {
    return (
      <div style={{ 
        padding: '24px',
        width: '100%',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#f8f9fa',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '1.2rem',
          fontFamily: "'Urbanist', Arial, sans-serif"
        }}>
          Loading Dashboard...
        </div>
      </div>
    );
  }

  return (
    <div
      className="dashboard-page"
      style={{
        padding: '24px',
        width: '100%',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#f8f9fa',
        minHeight: '100vh',
        overflow: 'hidden'
      }}
    >
      {/* Trial Expiry Notice */}
      <TrialExpiryNotice subscriptionPackage={subscriptionPackage} />

      {/* Top Stats Cards */}
      <StatsOverview dashboardData={dashboardData} />

      {/* Middle Row - Tables Side by Side */}
      <div
        className="dashboard-middle-row"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          gap: '24px',
          marginBottom: '24px'
        }}
      >
        <AssignedProjectsTable assignedProjects={assignedProjects} />
        {/* Use task start date to position tasks in the calendar */}
        <TodaysTasksCalendar todaysTasks={todaysTasks} dateField="start_date" />
      </div>

      {/* Bottom Row - Charts and Notifications */}
      <div
        className="dashboard-bottom-row"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          gap: '24px'
        }}
      >
        <TasksUpdateChart dashboardData={dashboardData} />
        <div
          className="dashboard-bottom-right"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px'
          }}
        >
          <DocumentStorageOverview dashboardData={dashboardData} />
          <NotificationsPanel notifications={notifications} />
        </div>
      </div>
    </div>
  );
}

export default Dashboard; 